// The ps-design house style, expressed as an MUI theme.
//
// This is a theme layer, not a rewrite: the components stay MUI, but their
// anatomy is re-cut to the house recipes -- buttons become chips, text fields
// become recessed wells, papers become cards. The numbers all come from
// ps-tokens.css; they are repeated here because MUI's `sx` cannot read CSS
// custom properties at theme-construction time.

import { createTheme } from '@mui/material/styles';

export const T = {
  bgDeep: '#0c0b0e',
  bgPanel: '#121014',
  well: '#080709',
  surface: '#19161d',
  surfaceTop: '#211d27',
  surfaceHi: '#272230',
  line: '#242028',
  lineSubtle: '#1a171f',
  borderHi: 'rgba(255, 255, 255, 0.063)',
  gold: '#e0ad48',
  goldBright: '#f5cd6e',
  goldFaint: 'rgba(224, 173, 72, 0.165)',
  magenta: '#d84cb4',
  steel: '#5b9fd6',
  mute: '#c25a44',
  solo: '#d4ba50',
  text: '#eef0f3',
  text2: '#9aa0ae',
  textDim: '#5c6170',
  font: '"Google Sans Flex", "Google Sans", Inter, system-ui, sans-serif',
  track: '0.09em',
  pad: 10,
  gap: 4,
  groupGap: 22,
  ease: 'cubic-bezier(0.22, 1, 0.36, 1)',
  t: '130ms',
  tSnap: '80ms',
};

// Composite an accent over an opaque base, at theme-construction time.
//
// The alternative, `color-mix(in srgb, ...)`, is fine in a browser but leaves
// no computed value in jsdom, so the opacity of a surface becomes untestable —
// and an accidentally transparent overlay is exactly the kind of defect that
// only a computed-style assertion catches.
export function mix(accent, base, ratio) {
  const channels = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const [ar, ag, ab] = channels(accent);
  const [br, bg, bb] = channels(base);
  const at = (a, b) => Math.round(a * ratio + b * (1 - ratio));
  return `rgb(${at(ar, br)}, ${at(ag, bg)}, ${at(ab, bb)})`;
}

// The caption voice: tracked uppercase micro-label. Applied to every field
// label, tab and button, so hierarchy comes from weight and colour rather than
// from six different font sizes.
export const label = {
  fontWeight: 700,
  fontSize: 10.5,
  letterSpacing: T.track,
  textTransform: 'uppercase',
  color: T.text2,
};

// The value voice: the regular cut, gold, slightly larger. The contrast
// between a quiet number and its loud little caption is the design.
export const value = {
  fontWeight: 400,
  fontSize: 13.5,
  letterSpacing: 0,
  color: T.gold,
  fontVariantNumeric: 'tabular-nums',
};

// The three depths, as `sx` recipes.
//
// Colour and gradient are set as separate properties on purpose: `background`
// is a shorthand that resets background-color, so a later `backgroundImage`
// leaves nothing but a border drawn around the page behind it. The opaque
// colour is the floor, the gradient is the lift -- and because the floor is a
// plain colour, a test can read it back.
export const surfaces = {
  panel: {
    backgroundColor: T.bgPanel,
    backgroundImage: `linear-gradient(${mix(T.surfaceTop, T.bgPanel, 0.6)}, ${T.bgPanel})`,
    border: `1px solid ${T.line}`,
    borderRadius: '6px',
    boxShadow: `inset 0 1px 0 ${T.borderHi}, 0 1.5px 0 rgba(0,0,0,0.4)`,
  },
  card: {
    backgroundColor: T.bgPanel,
    backgroundImage: `linear-gradient(${mix(T.surface, T.bgPanel, 0.65)}, ${mix(T.bgPanel, T.well, 0.65)})`,
    border: `1px solid ${T.line}`,
    borderRadius: '6px',
    boxShadow: `inset 0 1px 0 ${T.borderHi}`,
  },
  well: {
    backgroundColor: T.well,
    backgroundImage: 'linear-gradient(rgba(0,0,0,0.25), transparent 14px)',
    border: `1px solid ${T.lineSubtle}`,
    borderRadius: '5px',
  },
  // A panel that floats free of the page: same body, deeper shadow. Popovers,
  // the chat window, the unlock sheet.
  popover: {
    backgroundColor: T.bgPanel,
    backgroundImage: `linear-gradient(${mix(T.surfaceTop, T.bgPanel, 0.6)}, ${T.bgPanel})`,
    border: `1px solid ${T.line}`,
    borderRadius: '8px',
    boxShadow: `inset 0 1px 0 ${T.borderHi}, 0 18px 48px rgba(0,0,0,0.6)`,
  },
};

// The ambient backdrop: near-black, a faint warm lift at 30% height, a corner
// vignette. Painted first on every full-bleed surface so it never reads flat.
export const backdrop = {
  backgroundColor: T.bgDeep,
  backgroundImage: [
    'radial-gradient(66% 66% at 50% 30%, rgba(110,82,38,0.10) 0%, rgba(110,82,38,0.04) 45%, rgba(110,82,38,0.008) 78%, transparent 100%)',
    'radial-gradient(120% 120% at 50% 50%, transparent 55%, rgba(0,0,0,0.14) 80%, rgba(0,0,0,0.40) 100%)',
  ].join(', '),
};

export const psTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: T.gold, contrastText: '#1a1206' },
    secondary: { main: T.magenta },
    info: { main: T.steel },
    error: { main: T.mute },
    warning: { main: T.solo },
    success: { main: '#3f7d50' },
    background: { default: T.bgDeep, paper: T.bgPanel },
    text: { primary: T.text, secondary: T.text2, disabled: T.textDim },
    divider: T.line,
  },

  shape: { borderRadius: 6 },

  typography: {
    fontFamily: T.font,
    fontSize: 13,
    h4: { fontWeight: 700, fontSize: 18, letterSpacing: T.track, textTransform: 'uppercase' },
    h6: { fontWeight: 700, fontSize: 12, letterSpacing: T.track, textTransform: 'uppercase', color: T.text2 },
    body2: { fontSize: 12.5 },
    caption: { fontSize: 11, color: T.text2 },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: T.bgDeep },
        // Gold thumb on near-black, 7px -- the scrollbar is chrome, not data.
        '*::-webkit-scrollbar': { width: 7, height: 7 },
        '*::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.2)', borderRadius: 3.5 },
        '*::-webkit-scrollbar-thumb': { background: 'rgba(224,173,72,0.45)', borderRadius: 3.5 },
        '*::-webkit-scrollbar-thumb:hover': { background: 'rgba(224,173,72,0.75)' },
      },
    },

    // Every button is a chip: 30px, radius 5, gradient surface, accent as a
    // wash rather than a solid fill. Full-strength colour is reserved for data.
    MuiButton: {
      defaultProps: { disableElevation: true, variant: 'outlined' },
      styleOverrides: {
        root: {
          minHeight: 30,
          padding: '0 12px',
          borderRadius: 5,
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: T.track,
          textTransform: 'uppercase',
          transition: `color ${T.t} ${T.ease}, border-color ${T.t} ${T.ease}, background ${T.t} ${T.ease}`,
        },
        outlined: {
          background: `linear-gradient(${T.surfaceTop}, ${T.surface})`,
          borderColor: T.line,
          color: T.text2,
          boxShadow: `inset 0 1px 0 ${T.borderHi}`,
          '&:hover': {
            background: `linear-gradient(${T.surfaceHi}, ${T.surface})`,
            borderColor: T.line,
            color: T.text,
          },
        },
        // "Contained" is the lit state of a chip -- an accent wash at 16%, not
        // a neon block.
        contained: {
          background: `color-mix(in srgb, ${T.gold} 16%, transparent)`,
          border: `1px solid color-mix(in srgb, ${T.gold} 90%, transparent)`,
          color: T.gold,
          boxShadow: `inset 0 1px 0 ${T.borderHi}`,
          '&:hover': { background: `color-mix(in srgb, ${T.gold} 24%, transparent)` },
        },
        containedError: {
          background: `color-mix(in srgb, ${T.mute} 16%, transparent)`,
          border: `1px solid color-mix(in srgb, ${T.mute} 90%, transparent)`,
          color: T.mute,
          '&:hover': { background: `color-mix(in srgb, ${T.mute} 26%, transparent)` },
        },
        containedSecondary: {
          background: `color-mix(in srgb, ${T.magenta} 16%, transparent)`,
          border: `1px solid color-mix(in srgb, ${T.magenta} 90%, transparent)`,
          color: T.magenta,
          '&:hover': { background: `color-mix(in srgb, ${T.magenta} 26%, transparent)` },
        },
        // A quiet chip: no face until hover. Worn by destructive actions.
        text: {
          color: T.textDim,
          border: '1px solid transparent',
          '&:hover': {
            background: `linear-gradient(${T.surfaceHi}, ${T.surface})`,
            borderColor: T.line,
            color: T.text,
          },
        },
        sizeSmall: { minHeight: 24, fontSize: 10, padding: '0 8px' },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          color: T.textDim,
          transition: `color ${T.t} ${T.ease}, background ${T.t} ${T.ease}`,
          '&:hover': { color: T.text, background: T.surfaceHi },
        },
      },
    },

    // Inputs are wells: recessed floors sunk into the card above them.
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: T.well,
          borderRadius: 5,
          fontSize: 13.5,
          transition: `border-color ${T.t} ${T.ease}`,
          '& fieldset': { borderColor: T.lineSubtle },
          '&:hover fieldset': { borderColor: T.line },
          '&.Mui-focused fieldset': { borderColor: `color-mix(in srgb, ${T.gold} 70%, transparent)`, borderWidth: 1 },
          '&.Mui-disabled': { background: 'rgba(8,7,9,0.5)' },
        },
        input: { padding: '8px 10px' },
        multiline: { padding: 0 },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: { ...label, '&.Mui-focused': { color: T.gold } },
      },
    },

    MuiFormHelperText: {
      styleOverrides: { root: { fontSize: 10.5, color: T.textDim, marginLeft: 2 } },
    },

    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: T.line },
      },
    },

    // Popovers and menus sit a radius step above panels.
    //
    // `backgroundColor` and `backgroundImage` are set separately on purpose.
    // The `background` shorthand resets background-color to transparent, so
    // pairing it with the `backgroundImage: 'none'` that kills MUI's dark-mode
    // elevation overlay leaves the paper fully see-through. The opaque colour
    // is also the floor if anything ever strips the gradient again.
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: T.bgPanel,
          backgroundImage: `linear-gradient(color-mix(in srgb, ${T.surfaceTop} 60%, ${T.bgPanel}), ${T.bgPanel})`,
          border: `1px solid ${T.line}`,
          borderRadius: 8,
          boxShadow: `inset 0 1px 0 ${T.borderHi}, 0 18px 48px rgba(0,0,0,0.6)`,
        },
      },
    },
    // The scrim, at the house value. A readable overlay needs both halves:
    // an opaque sheet and enough separation from what is behind it.
    //
    // `invisible` has to be restated. Menus and selects mount the same
    // Backdrop with the invisible flag, and MUI's own rule for that flag does
    // not outrank an override on `root` -- so styling root alone dimmed the
    // entire page every time a dropdown opened.
    MuiBackdrop: {
      styleOverrides: {
        root: { backgroundColor: 'rgba(0, 0, 0, 0.62)' },
        invisible: { backgroundColor: 'transparent' },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontWeight: 700, fontSize: 12, letterSpacing: T.track, textTransform: 'uppercase', color: T.text2 },
      },
    },
    MuiDialogContent: { styleOverrides: { dividers: { borderColor: T.lineSubtle } } },

    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: T.surface,
          backgroundImage: 'none',
          border: `1px solid ${T.line}`,
          borderRadius: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 13,
          minHeight: 30,
          '&:hover': { background: `linear-gradient(rgba(216,76,180,0.28), rgba(216,76,180,0.14))`, color: '#fff' },
          '&.Mui-selected': { background: `color-mix(in srgb, ${T.magenta} 20%, transparent)` },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 34, borderBottom: `1px solid ${T.lineSubtle}` },
        indicator: { height: 2, background: T.gold },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          ...label,
          minHeight: 34,
          padding: '0 12px',
          transition: `color ${T.t} ${T.ease}`,
          '&:hover': { color: T.text },
          '&.Mui-selected': { color: T.gold },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          height: 24,
          borderRadius: 999,
          fontWeight: 700,
          fontSize: 10.5,
          letterSpacing: T.track,
          textTransform: 'uppercase',
          background: `linear-gradient(${T.surfaceTop}, ${T.surface})`,
          border: `1px solid ${T.line}`,
          color: T.text2,
        },
        deleteIcon: { fontSize: 14, color: T.textDim, '&:hover': { color: T.mute } },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 5, fontSize: 12.5, border: '1px solid', alignItems: 'flex-start' },
        // The accent is still a wash, but composited against an opaque panel
        // rather than left translucent -- these also fly as snackbars, where a
        // translucent tint sits directly over the page and cannot be read.
        standardError: {
          backgroundColor: mix(T.mute, T.bgPanel, 0.13),
          borderColor: 'rgba(194,90,68,0.5)',
          color: '#e9a495',
        },
        standardWarning: {
          backgroundColor: mix(T.solo, T.bgPanel, 0.11),
          borderColor: 'rgba(212,186,80,0.45)',
          color: '#e2d091',
        },
        standardSuccess: {
          backgroundColor: mix('#3f7d50', T.bgPanel, 0.15),
          borderColor: 'rgba(63,125,80,0.5)',
          color: '#9ed0ad',
        },
        standardInfo: {
          backgroundColor: mix(T.steel, T.bgPanel, 0.13),
          borderColor: 'rgba(91,159,214,0.45)',
          color: '#a9cfee',
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: { root: { fontWeight: 700, fontSize: 11, letterSpacing: T.track, textTransform: 'uppercase' } },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          background: 'transparent',
          border: `1px solid ${T.lineSubtle}`,
          borderRadius: 6,
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: { minHeight: 34, '&.Mui-expanded': { minHeight: 34 } },
        content: { margin: '8px 0', '&.Mui-expanded': { margin: '8px 0' } },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: { width: 38, height: 22, padding: 0, display: 'flex' },
        switchBase: {
          padding: 3,
          color: T.textDim,
          '&.Mui-checked': { transform: 'translateX(16px)', color: T.gold },
          '&.Mui-checked + .MuiSwitch-track': {
            background: `color-mix(in srgb, ${T.gold} 26%, transparent)`,
            borderColor: `color-mix(in srgb, ${T.gold} 70%, transparent)`,
            opacity: 1,
          },
        },
        thumb: { width: 16, height: 16, boxShadow: 'none' },
        track: {
          borderRadius: 999,
          background: T.well,
          border: `1px solid ${T.line}`,
          opacity: 1,
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: { height: 6, borderRadius: 999, background: T.well, border: `1px solid ${T.lineSubtle}` },
        bar: { borderRadius: 999, background: T.gold },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: T.surface,
          border: `1px solid ${T.line}`,
          borderRadius: 5,
          fontSize: 11,
          color: T.text,
        },
      },
    },

    MuiDivider: { styleOverrides: { root: { borderColor: T.lineSubtle } } },
  },
});

export default psTheme;
